import { AiFormDefinition, AiFormField } from './ai-form.types';
import { fetchAiFormOptions, filterAiFormOptions } from './ai-form-field-callers';
import { dateValue, numberValue, templateBooleanValue, templateReferenceValue } from './ai-form-field-processing';
import { budgetProjectId } from './budget-form-processing';

interface TemplateField {
    id: string;
    modelKey: string;
    scope: 'budget' | 'rows';
    fieldType: string;
    label: string;
    required: boolean;
    readOnly: boolean;
    listType?: string;
    custom: boolean;
    unsupportedReason?: string;
}

interface BudgetMetadata {
    projectId: string;
    templateId: string;
    fields: TemplateField[];
    options: Record<string, { id: any; name: string }[]>;
}

function templateField(item: TemplateField, metadata: BudgetMetadata): AiFormField {
    const options = metadata.options[item.listType] ?? [];
    const boolean = item.fieldType === 'BOOLEAN' || item.fieldType === 'CHECKBOX' || item.listType === 'BOOL';
    const field: AiFormField = {
        key: `custom_${item.id}`, modelKey: item.modelKey, label: item.label || item.modelKey,
        type: boolean ? 'boolean' : item.fieldType === 'NUMBER' ? 'number' : item.fieldType === 'DATE' ? 'date' : 'string',
        required: item.required, readOnly: item.readOnly || !!item.unsupportedReason,
        ...(item.unsupportedReason ? { readOnlyReason: item.unsupportedReason } : {}),
        nullable: true, maxLength: 4096,
    };
    if (boolean) field.processing = templateBooleanValue(options, item.scope === 'rows' && item.listType === 'BOOL');
    else if (field.type === 'number') field.processing = numberValue;
    else if (field.type === 'date') field.processing = dateValue;
    else if (item.fieldType === 'LIST') {
        field.caller = async query => filterAiFormOptions(options, query);
        field.processing = templateReferenceValue(options, item.scope === 'rows');
    }
    return field;
}

export const resolveBudgetFields: AiFormDefinition['resolveFields'] = async (fields, { values, state, signal, requests }) => {
    const requested = values['projectId'] ?? state?.values['projectId'];
    if (requested == null) return fields;
    const projectId = budgetProjectId(requested);
    const current = state?.metadata?.['budget'] as BudgetMetadata | undefined;
    if (state && String(state.values['projectId']) === projectId && !current) return fields;
    const metadata: BudgetMetadata = current?.projectId === projectId ? current
        : await fetchAiFormOptions(`/core/projects/${projectId}/budgets/new/form-metadata`, signal, requests);
    if (String(metadata.projectId) !== projectId || !Array.isArray(metadata.fields) || !metadata.options) {
        throw new Error('Не удалось получить схему шаблона бюджета');
    }
    const custom = metadata.fields.filter(item => item.custom);
    return fields.map(field => {
        if (field.key !== 'rows') return field;
        return { ...field, items: field.items.map(item => {
            const list = item.key === 'unitId' ? 'Unit' : item.key === 'categoryId' ? 'Category' : null;
            return list && metadata.options[list] ? { ...item,
                caller: async query => filterAiFormOptions(metadata.options[list], query),
            } : item;
        }).concat(custom.filter(item => item.scope === 'rows').map(item => templateField(item, metadata))) };
    }).concat(custom.filter(item => item.scope === 'budget').map(item => templateField(item, metadata)));
};
